const ANALYTICS_QUESTION_HISTORY_STORAGE_KEY_PREFIX = 'portals:analytics-question-history'

export const MAX_ANALYTICS_QUESTION_HISTORY_ENTRIES = 10
// Limit stale questions in long-lived sessions independently of logout cleanup and the entry cap.
export const ANALYTICS_QUESTION_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export type AnalyticsQuestionHistoryEntry = Readonly<{
  question: string
  askedAt: number
}>

function isAnalyticsQuestionHistoryEntry(value: unknown): value is AnalyticsQuestionHistoryEntry {
  if (typeof value !== 'object' || value === null) return false

  const entry = value as Record<string, unknown>
  return (
    typeof entry.question === 'string' &&
    entry.question.trim().length > 0 &&
    typeof entry.askedAt === 'number' &&
    Number.isFinite(entry.askedAt)
  )
}

function isFreshHistoryEntry(askedAt: number, now: number): boolean {
  return now - askedAt <= ANALYTICS_QUESTION_HISTORY_MAX_AGE_MS
}

function getValidHistoryEntries(
  values: readonly unknown[],
  now = Date.now(),
): AnalyticsQuestionHistoryEntry[] {
  return values
    .filter(isAnalyticsQuestionHistoryEntry)
    .filter(({ askedAt }) => isFreshHistoryEntry(askedAt, now))
    .map(({ question, askedAt }) => ({
      question,
      askedAt,
    }))
}

export function getAnalyticsQuestionHistoryStorageKey(memberId: string): string {
  return `${ANALYTICS_QUESTION_HISTORY_STORAGE_KEY_PREFIX}:${memberId}`
}

export function recordAnalyticsQuestionHistory(
  entries: readonly AnalyticsQuestionHistoryEntry[],
  question: string,
  now = Date.now(),
): AnalyticsQuestionHistoryEntry[] {
  const trimmedQuestion = question.trim()
  if (!trimmedQuestion) return mergeAnalyticsQuestionHistory(entries, [], now)

  return mergeAnalyticsQuestionHistory([{ question: trimmedQuestion, askedAt: now }], entries, now)
}

export function withoutAnalyticsQuestionHistoryEntry(
  entries: readonly AnalyticsQuestionHistoryEntry[],
  entry: AnalyticsQuestionHistoryEntry,
  now = Date.now(),
): AnalyticsQuestionHistoryEntry[] {
  return mergeAnalyticsQuestionHistory(
    entries.filter(
      (recent) => recent.question !== entry.question || recent.askedAt !== entry.askedAt,
    ),
    [],
    now,
  )
}

export function mergeAnalyticsQuestionHistory(
  current: readonly AnalyticsQuestionHistoryEntry[],
  stored: readonly AnalyticsQuestionHistoryEntry[],
  now = Date.now(),
): AnalyticsQuestionHistoryEntry[] {
  const seen = new Set<string>()
  const merged: AnalyticsQuestionHistoryEntry[] = []

  for (const entry of [...current, ...stored]
    .filter(({ askedAt }) => isFreshHistoryEntry(askedAt, now))
    .sort((first, second) => second.askedAt - first.askedAt)) {
    const question = entry.question.trim()
    if (!question || seen.has(question)) continue

    seen.add(question)
    merged.push({ question, askedAt: entry.askedAt })
    if (merged.length === MAX_ANALYTICS_QUESTION_HISTORY_ENTRIES) break
  }

  return merged
}

export function loadAnalyticsQuestionHistory(memberId: string): AnalyticsQuestionHistoryEntry[] {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(getAnalyticsQuestionHistoryStorageKey(memberId))
    if (!raw) return []

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []

    return mergeAnalyticsQuestionHistory([], getValidHistoryEntries(parsed))
  } catch {
    return []
  }
}

export function saveAnalyticsQuestionHistory(
  memberId: string,
  entries: readonly AnalyticsQuestionHistoryEntry[],
): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      getAnalyticsQuestionHistoryStorageKey(memberId),
      JSON.stringify(mergeAnalyticsQuestionHistory(entries, [])),
    )
  } catch {
    // Browser storage can be unavailable or full. Keep the in-memory history usable.
  }
}

export function removeAnalyticsQuestionHistory(memberId: string): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(getAnalyticsQuestionHistoryStorageKey(memberId))
  } catch {
    // Browser storage can be unavailable. Keep the in-memory history usable.
  }
}

export function clearAllAnalyticsQuestionHistory(): void {
  if (typeof window === 'undefined') return

  try {
    const keysToRemove: string[] = []
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (key?.startsWith(`${ANALYTICS_QUESTION_HISTORY_STORAGE_KEY_PREFIX}:`)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  } catch {
    // Browser storage can be unavailable. Logout and session expiry still continue.
  }
}
